import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";

const AuthContext = createContext({
  user: null,
  loading: true,
  policies: { version: null, url: null, change_note: "" },
  refresh: () => {},
  logout: () => {},
});

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [policies, setPolicies] = useState({ version: null, url: null, change_note: "" });

  const checkAuth = useCallback(async () => {
    try {
      const r = await api.get("/auth/me");
      setUser(r.data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadPoliciesVersion = useCallback(async () => {
    try {
      const r = await api.get("/policies/current-version");
      setPolicies({
        version: r.data?.version || null,
        url: r.data?.url || null,
        change_note: r.data?.change_note || "",
      });
    } catch {
      // keep defaults
    }
  }, []);

  useEffect(() => {
    // CRITICAL: skip /me check if returning from OAuth (AuthCallback will set session first)
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
    loadPoliciesVersion();
  }, [checkAuth, loadPoliciesVersion]);

  const logout = useCallback(async () => {
    try { await api.post("/auth/logout"); } catch {}
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider value={{ user, setUser, loading, policies, refresh: checkAuth, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
